/**
 * KIPU MR numbers — see ATH-267.
 *
 * An MR number names one KIPU chart: a location prefix, the year, and a sequence that restarts each
 * year and repeats across locations (`RRC-2026-91`, `RR-2025-64`). KIPU itself writes the same
 * number with and without the hyphen after the prefix (`RRA2026-39`, `RRA-2026-4`), so a number a
 * coordinator types and the one on a KIPU chart can be the same chart spelled two ways.
 *
 * `normalizeMrNumber` gives every spelling one form, so they compare equal. The year is always four
 * digits, which is what makes this safe: adding or dropping a hyphen cannot turn one number into
 * another. Every reader of an MR number — the client import, the admin CSV check, and the KIPU sync
 * when it matches by MR number — goes through it.
 */

/** Prefix (letters), four-digit year, sequence — each hyphen optional. */
const MR_NUMBER_PATTERN = /^([A-Z]+)-?(\d{4})-?(\d+)$/;

/**
 * `PREFIX-YEAR-SEQUENCE`, uppercase, or undefined for anything that is not an MR number.
 *
 * The sequence keeps its digits as written: no MR number has been seen with a leading zero, and
 * dropping one would be a guess.
 */
export const normalizeMrNumber = (value: string | null | undefined): string | undefined => {
    const match = MR_NUMBER_PATTERN.exec((value ?? "").trim().toUpperCase());
    if (!match) return undefined;
    const [
        ,
        prefix,
        year,
        sequence,
    ] = match;
    return `${prefix}-${year}-${sequence}`;
};
